import ApplicationController from "../../../application_controller"
import { autoUpdate, computePosition, flip, offset, shift } from "@floating-ui/dom"
import {
  RANGE_PRESETS, addDays, addMonths, boundedPreset, calendarDays, clampDay, compareDays, formatDate, formatRange, outOfRange,
  parseDateInput, parseDateTimeInput, parseISODate, parseISOTime, parseRangeInput, parseTimeInput,
  sameDay, startOfDay, timeSlots, toISODate, toISOTime
} from "./date_logic"

const WEEK_START = 1

export default class extends ApplicationController {
  static targets = ["value", "endValue", "input", "clear", "toggle", "popover"]
  static values = {
    mode: { type: String, default: "date" },
    pattern: { type: String, default: "%d.%m.%Y" },
    monthNames: Array,
    locale: { type: String, default: "en" },
    min: String,
    max: String,
    minuteStep: { type: Number, default: 15 },
    defaultTime: { type: String, default: "00:00" },
    autoSubmit: Boolean,
    messages: Object
  }

  connect() {
    this.min = parseISODate(this.minValue)
    this.max = parseISODate(this.maxValue)
    this.readHiddenValues()
    this.view = "days"
    this.onOutsidePointer = this.onOutsidePointer.bind(this)
    this.onFocusOut = this.onFocusOut.bind(this)
    this.element.addEventListener("focusout", this.onFocusOut)
  }

  disconnect() {
    this.close({ restoreFocus: false })
    this.element.removeEventListener("focusout", this.onFocusOut)
  }

  readHiddenValues() {
    const value = this.valueTarget.value
    this.date = this.modeValue === "time" ? null : parseISODate(value)
    this.time = parseISOTime(value) || null
    if (this.isRange) {
      this.start = parseISODate(value)
      this.end = this.hasEndValueTarget ? parseISODate(this.endValueTarget.value) : null
    }
    this.lastCommitted = this.formatted()
  }

  get isRange() { return this.modeValue === "range" }
  get hasCalendar() { return this.modeValue !== "time" }
  get hasTimeList() { return this.modeValue === "time" || this.modeValue === "datetime" }
  get isOpen() { return this.popoverTarget.matches(":popover-open") }
  get today() { return startOfDay(new Date()) }

  get defaultTime() {
    return parseISOTime(this.defaultTimeValue) || { hours: 0, minutes: 0 }
  }

  // ---- open / close ----

  toggle() {
    this.isOpen ? this.close() : this.open({ focus: true })
  }

  open({ focus = false } = {}) {
    if (this.isOpen || this.inputTarget.disabled) return
    this.view = "days"
    this.anchor = null
    this.hover = null
    this.cursor = clampDay(this.selectedDay() || this.today, this.min, this.max)
    this.month = new Date(this.cursor.getFullYear(), this.cursor.getMonth(), 1)
    this.render()
    this.popoverTarget.showPopover()
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.startPositioning()
    this.scrollTimeIntoView()
    document.addEventListener("pointerdown", this.onOutsidePointer, true)
    if (focus) this.focusPanel()
  }

  close({ restoreFocus = true } = {}) {
    if (!this.isOpen) return
    this.popoverTarget.hidePopover()
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.cleanupPositioning?.()
    this.cleanupPositioning = null
    document.removeEventListener("pointerdown", this.onOutsidePointer, true)
    this.anchor = null
    if (restoreFocus) this.inputTarget.focus({ preventScroll: true })
  }

  startPositioning() {
    this.cleanupPositioning = autoUpdate(this.inputTarget.parentElement, this.popoverTarget, () => {
      computePosition(this.inputTarget.parentElement, this.popoverTarget, {
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [offset(6), flip({ padding: 8 }), shift({ padding: 8 })]
      }).then(({ x, y }) => Object.assign(this.popoverTarget.style, { left: `${x}px`, top: `${y}px` }))
    })
  }

  onOutsidePointer(event) {
    if (!this.element.contains(event.target)) this.close({ restoreFocus: false })
  }

  onFocusOut(event) {
    if (event.relatedTarget && !this.element.contains(event.relatedTarget)) this.close({ restoreFocus: false })
  }

  // Clicks in the panel keep focus in the text field, so typing can continue after picking with the mouse.
  keepFocus(event) {
    event.preventDefault()
  }

  focusPanel() {
    const target = this.hasCalendar
      ? this.popoverTarget.querySelector("[data-day][tabindex='0']")
      : this.popoverTarget.querySelector("[data-time][aria-selected='true']") || this.popoverTarget.querySelector("[data-time]")
    target?.focus({ preventScroll: !this.hasCalendar })
  }

  // ---- typing ----

  inputKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.isOpen ? this.focusPanel() : this.open({ focus: true })
        break
      case "Enter":
        this.commitTyped()
        // Closed: let Enter submit the surrounding form, now holding the typed value.
        if (this.isOpen) {
          event.preventDefault()
          this.close()
        }
        break
      case "Escape":
        if (this.isOpen) {
          event.preventDefault()
          event.stopPropagation()
          this.inputTarget.value = this.lastCommitted
          this.close()
        }
        break
      case "Tab":
        this.close({ restoreFocus: false })
        break
    }
  }

  // While typing, the open calendar follows along to the date being typed.
  preview() {
    const parsed = this.parseText(this.inputTarget.value)
    const day = parsed && (parsed.date || parsed.start || parsed.end)
    if (!this.isOpen || !day) return
    this.cursor = day
    this.month = new Date(day.getFullYear(), day.getMonth(), 1)
    this.render()
  }

  commitTyped() {
    const text = this.inputTarget.value.trim()
    if (text === this.lastCommitted) return
    if (text === "") {
      this.clear()
      return
    }

    const parsed = this.parseText(text)
    const days = parsed ? [parsed.date, parsed.start, parsed.end].filter(Boolean) : []
    if (!parsed || days.some(day => outOfRange(day, this.min, this.max))) {
      this.rejectTyped()
      return
    }

    Object.assign(this, parsed)
    this.commit()
    if (this.isOpen) this.render()
  }

  parseText(text) {
    const options = { pattern: this.patternValue, today: new Date(), monthNames: this.monthNamesValue }
    switch (this.modeValue) {
      case "time": {
        const time = parseTimeInput(text)
        return time && { time }
      }
      case "datetime":
        return parseDateTimeInput(text, { ...options, defaultTime: this.time || this.defaultTime })
      case "range":
        return parseRangeInput(text, options)
      default: {
        const date = parseDateInput(text, options)
        return date && { date }
      }
    }
  }

  rejectTyped() {
    this.inputTarget.value = this.lastCommitted
    const field = this.inputTarget.parentElement
    field.classList.add("border-red!")
    this.inputTarget.setAttribute("title", this.messagesValue.invalid)
    setTimeout(() => {
      field.classList.remove("border-red!")
      this.inputTarget.removeAttribute("title")
    }, 1500)
  }

  // ---- committing ----

  formatted() {
    switch (this.modeValue) {
      case "time": return toISOTime(this.time)
      case "datetime": return this.date ? `${formatDate(this.date, this.patternValue, this.monthNamesValue)} ${toISOTime(this.time || this.defaultTime)}` : ""
      case "range": return formatRange(this.start, this.end, this.patternValue, this.monthNamesValue)
      default: return formatDate(this.date, this.patternValue, this.monthNamesValue)
    }
  }

  commit({ close = false } = {}) {
    const previous = [this.valueTarget.value, this.hasEndValueTarget ? this.endValueTarget.value : null]

    switch (this.modeValue) {
      case "time": this.valueTarget.value = toISOTime(this.time); break
      case "datetime": this.valueTarget.value = this.date ? `${toISODate(this.date)}T${toISOTime(this.time || this.defaultTime)}` : ""; break
      case "range":
        this.valueTarget.value = toISODate(this.start)
        if (this.hasEndValueTarget) this.endValueTarget.value = toISODate(this.end)
        break
      default: this.valueTarget.value = toISODate(this.date)
    }

    this.lastCommitted = this.formatted()
    this.inputTarget.value = this.lastCommitted
    if (this.hasClearTarget) this.clearTarget.hidden = this.lastCommitted === ""
    if (close) this.close()

    const changed = previous[0] !== this.valueTarget.value || (this.hasEndValueTarget && previous[1] !== this.endValueTarget.value)
    if (!changed) return
    this.valueTarget.dispatchEvent(new Event("change", { bubbles: true }))
    if (this.hasEndValueTarget) this.endValueTarget.dispatchEvent(new Event("change", { bubbles: true }))
    if (this.autoSubmitValue) this.valueTarget.form?.requestSubmit()
  }

  clear(event) {
    event?.preventDefault()
    Object.assign(this, { date: null, time: null, start: null, end: null, anchor: null })
    this.commit()
    if (this.isOpen) {
      this.close()
    } else {
      this.inputTarget.focus({ preventScroll: true })
    }
  }

  selectedDay() {
    return this.isRange ? this.start || this.end : this.date
  }

  // ---- picking ----

  pickDay(day) {
    if (outOfRange(day, this.min, this.max)) return
    this.cursor = day

    if (this.isRange) {
      if (!this.anchor) {
        this.anchor = day
        this.hover = day
        this.render()
        return
      }
      const [start, end] = compareDays(this.anchor, day) <= 0 ? [this.anchor, day] : [day, this.anchor]
      Object.assign(this, { start, end, anchor: null })
      this.commit({ close: true })
      return
    }

    this.date = day
    if (this.modeValue === "datetime") {
      this.time ||= this.defaultTime
      this.commit()
      this.render()
    } else {
      this.commit({ close: true })
    }
  }

  pickTime(time) {
    this.time = time
    if (this.modeValue === "datetime") this.date ||= clampDay(this.today, this.min, this.max)
    this.commit({ close: true })
  }

  pickToday() {
    if (this.modeValue === "datetime") {
      const now = new Date()
      this.date = clampDay(this.today, this.min, this.max)
      this.time = { hours: now.getHours(), minutes: now.getMinutes() }
      this.commit({ close: true })
    } else {
      this.pickDay(this.today)
    }
  }

  pickPreset(key) {
    const range = boundedPreset(key, new Date(), this.min, this.max)
    if (!range) return
    this.start = range.start
    this.end = range.end
    this.anchor = null
    this.commit({ close: true })
  }

  // ---- calendar keyboard ----

  gridKeydown(event) {
    const moves = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 }
    let next = null
    if (event.key in moves) next = addDays(this.cursor, moves[event.key])
    else if (event.key === "PageUp" || event.key === "PageDown") next = addMonths(this.cursor, (event.key === "PageUp" ? -1 : 1) * (event.shiftKey ? 12 : 1))
    else if (event.key === "Home") next = addDays(this.cursor, -((this.cursor.getDay() - WEEK_START + 7) % 7))
    else if (event.key === "End") next = addDays(this.cursor, 6 - ((this.cursor.getDay() - WEEK_START + 7) % 7))
    else if (event.key === "Enter" || event.key === " ") {
      event.preventDefault()
      this.pickDay(this.cursor)
      if (this.isOpen) this.focusPanel()
      return
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.close()
      return
    } else {
      return
    }

    event.preventDefault()
    this.cursor = clampDay(next, this.min, this.max)
    this.month = new Date(this.cursor.getFullYear(), this.cursor.getMonth(), 1)
    if (this.anchor) this.hover = this.cursor
    this.render()
    this.focusPanel()
  }

  timeKeydown(event) {
    const buttons = Array.from(this.popoverTarget.querySelectorAll("[data-time]"))
    const index = buttons.indexOf(document.activeElement)
    const moves = { ArrowDown: 1, ArrowUp: -1, PageDown: 4, PageUp: -4 }
    if (event.key in moves) {
      event.preventDefault()
      buttons[Math.max(0, Math.min(buttons.length - 1, index + moves[event.key]))]?.focus()
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      this.close()
    }
  }

  // ---- rendering ----

  render() {
    const nodes = []
    if (this.isRange) nodes.push(this.presets())
    if (this.hasCalendar) nodes.push(this.calendar())
    if (this.hasTimeList) nodes.push(this.timeList())
    this.popoverTarget.replaceChildren(...nodes)
  }

  calendar() {
    const wrapper = this.el("div", "w-[18rem] p-3")
    wrapper.append(this.calendarHeader())
    wrapper.append(this.view === "days" ? this.dayGrid() : this.monthGrid())
    wrapper.append(this.calendarFooter())
    return wrapper
  }

  calendarHeader() {
    const header = this.el("div", "mb-2 flex items-center justify-between gap-1")
    const months = this.view === "months"
    const step = months ? 12 : 1
    const previousMonth = addMonths(this.month, -step)
    const nextMonth = addMonths(this.month, step)
    const lastOfPrevious = months ? new Date(previousMonth.getFullYear(), 11, 31) : new Date(previousMonth.getFullYear(), previousMonth.getMonth() + 1, 0)
    const firstOfNext = months ? new Date(nextMonth.getFullYear(), 0, 1) : nextMonth

    header.append(this.navButton("M15 6l-6 6 6 6", this.messagesValue[months ? "previous_year" : "previous_month"],
      !!this.min && compareDays(lastOfPrevious, this.min) < 0, () => { this.month = previousMonth; this.render() }))

    const title = this.el("button", "flex items-center gap-1 rounded-lg px-2 py-1 text-sm font-semibold text-gray-900 hover:bg-gray-100 cursor-pointer")
    title.type = "button"
    title.setAttribute("aria-live", "polite")
    title.textContent = months
      ? String(this.month.getFullYear())
      : new Intl.DateTimeFormat(this.localeValue, { month: "long", year: "numeric" }).format(this.month)
    title.append(this.icon(months ? "M6 15l6-6 6 6" : "M6 9l6 6 6-6", "size-3.5 text-gray-400"))
    title.addEventListener("click", () => { this.view = months ? "days" : "months"; this.render() })
    header.append(title)

    header.append(this.navButton("M9 6l6 6-6 6", this.messagesValue[months ? "next_year" : "next_month"],
      !!this.max && compareDays(firstOfNext, this.max) > 0, () => { this.month = nextMonth; this.render() }))
    return header
  }

  navButton(path, label, disabled, onClick) {
    const button = this.el("button", "flex size-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 cursor-pointer disabled:opacity-30 disabled:cursor-default disabled:hover:bg-transparent")
    button.type = "button"
    button.disabled = disabled
    button.setAttribute("aria-label", label)
    button.append(this.icon(path, "size-4"))
    button.addEventListener("click", onClick)
    return button
  }

  dayGrid() {
    const container = this.el("div")
    const weekdays = this.el("div", "grid grid-cols-7 mb-1")
    const weekdayFormat = new Intl.DateTimeFormat(this.localeValue, { weekday: "short" })
    for (let i = 0; i < 7; i++) {
      const label = this.el("span", "py-1 text-center text-[0.6875rem] font-medium uppercase tracking-wide text-gray-400")
      label.textContent = weekdayFormat.format(new Date(2024, 0, 1 + ((WEEK_START + i + 6) % 7))).replace(/\.$/, "").slice(0, 2)
      label.setAttribute("aria-hidden", "true")
      weekdays.append(label)
    }
    container.append(weekdays)

    const grid = this.el("div", "grid grid-cols-7 gap-y-0.5")
    grid.setAttribute("role", "group")
    grid.setAttribute("aria-label", new Intl.DateTimeFormat(this.localeValue, { month: "long", year: "numeric" }).format(this.month))
    grid.addEventListener("keydown", event => this.gridKeydown(event))
    const fullFormat = new Intl.DateTimeFormat(this.localeValue, { dateStyle: "full" })
    const [rangeStart, rangeEnd] = this.visibleRange()

    calendarDays(this.month.getFullYear(), this.month.getMonth(), WEEK_START).forEach(({ date, inMonth }) => {
      const isStart = sameDay(date, rangeStart)
      const isEnd = sameDay(date, rangeEnd)
      const between = rangeStart && rangeEnd && compareDays(date, rangeStart) > 0 && compareDays(date, rangeEnd) < 0
      const band = this.anchor ? "to-secondary-50" : "to-secondary-100"
      let cellClass = "flex justify-center"
      if (rangeStart && rangeEnd && !sameDay(rangeStart, rangeEnd)) {
        if (between) cellClass += this.anchor ? " bg-secondary-50" : " bg-secondary-100"
        else if (isStart) cellClass += ` bg-linear-to-r from-transparent from-50% ${band} to-50%`
        else if (isEnd) cellClass += ` bg-linear-to-l from-transparent from-50% ${band} to-50%`
      }
      const cell = this.el("div", cellClass)

      const selected = this.isRange ? (isStart || isEnd) : sameDay(date, this.date)
      const disabled = outOfRange(date, this.min, this.max)
      const today = sameDay(date, this.today)
      let classes = "relative size-9 rounded-lg text-sm tabular-nums transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/60 "
      if (disabled) classes += "text-gray-300 line-through cursor-not-allowed"
      else if (selected) classes += "bg-secondary text-white font-semibold cursor-pointer"
      else classes += `${inMonth ? "text-gray-800" : "text-gray-300"} ${today ? "font-semibold text-primary-600" : ""} hover:bg-gray-100 cursor-pointer`
      if (today) classes += " after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:size-1 after:rounded-full " + (selected ? "after:bg-white" : "after:bg-primary")

      const button = this.el("button", classes)
      button.type = "button"
      button.textContent = date.getDate()
      button.dataset.day = toISODate(date)
      button.tabIndex = sameDay(date, this.cursor) ? 0 : -1
      button.disabled = disabled
      button.setAttribute("aria-label", fullFormat.format(date))
      button.setAttribute("aria-pressed", String(selected))
      if (today) button.setAttribute("aria-current", "date")
      button.addEventListener("click", () => this.pickDay(date))
      if (this.isRange) button.addEventListener("mouseenter", () => this.hoverDay(date))
      cell.append(button)
      grid.append(cell)
    })
    container.append(grid)
    return container
  }

  // While picking a range, the band follows the pointer from the first click.
  visibleRange() {
    if (!this.isRange) return [null, null]
    if (this.anchor) {
      const other = this.hover || this.anchor
      return compareDays(this.anchor, other) <= 0 ? [this.anchor, other] : [other, this.anchor]
    }
    return [this.start, this.end]
  }

  hoverDay(date) {
    if (!this.anchor || sameDay(this.hover, date)) return
    this.hover = date
    this.render()
  }

  monthGrid() {
    const grid = this.el("div", "grid grid-cols-3 gap-1.5 py-1")
    const format = new Intl.DateTimeFormat(this.localeValue, { month: "short" })
    const selected = this.selectedDay()
    for (let month = 0; month < 12; month++) {
      const first = new Date(this.month.getFullYear(), month, 1)
      const last = new Date(this.month.getFullYear(), month + 1, 0)
      const disabled = (!!this.max && compareDays(first, this.max) > 0) || (!!this.min && compareDays(last, this.min) < 0)
      const isSelected = selected && selected.getFullYear() === first.getFullYear() && selected.getMonth() === month
      const button = this.el("button", `h-12 rounded-lg text-sm capitalize transition-colors cursor-pointer disabled:text-gray-300 disabled:cursor-not-allowed ${isSelected ? "bg-secondary text-white font-semibold" : "text-gray-800 hover:bg-gray-100"}`)
      button.type = "button"
      button.disabled = disabled
      button.textContent = format.format(first).replace(/\.$/, "")
      button.addEventListener("click", () => {
        this.month = first
        this.cursor = clampDay(new Date(first.getFullYear(), month, Math.min(this.cursor.getDate(), last.getDate())), this.min, this.max)
        this.view = "days"
        this.render()
      })
      grid.append(button)
    }
    return grid
  }

  calendarFooter() {
    const footer = this.el("div", "mt-2 flex items-center justify-between gap-2 border-t border-gray-100 pt-2")
    if (this.isRange) {
      const hint = this.el("span", "text-xs text-gray-500")
      hint.textContent = this.anchor ? this.messagesValue.pick_end : this.messagesValue.range_hint
      footer.append(hint)
    } else {
      const today = this.footerButton(this.messagesValue[this.modeValue === "datetime" ? "now" : "today"], () => this.pickToday())
      today.disabled = outOfRange(this.today, this.min, this.max)
      footer.append(today)
    }
    if (this.hasClearTarget && this.formatted() !== "") footer.append(this.footerButton(this.messagesValue.clear, () => this.clear(), "text-gray-500"))
    return footer
  }

  footerButton(text, onClick, tone = "text-secondary-700") {
    const button = this.el("button", `rounded-md px-2 py-1 text-xs font-medium ${tone} hover:bg-gray-100 cursor-pointer disabled:opacity-40 disabled:cursor-default`)
    button.type = "button"
    button.textContent = text
    button.addEventListener("click", onClick)
    return button
  }

  presets() {
    const list = this.el("div", "flex gap-1 overflow-x-auto border-b border-gray-100 p-2 sm:w-40 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r")
    RANGE_PRESETS.forEach(key => {
      const range = boundedPreset(key, new Date(), this.min, this.max)
      if (!range) return
      const active = sameDay(range.start, this.start) && sameDay(range.end, this.end)
      const button = this.el("button", `whitespace-nowrap rounded-md px-2.5 py-1.5 text-left text-sm transition-colors cursor-pointer ${active ? "bg-secondary-50 font-medium text-secondary" : "text-gray-700 hover:bg-gray-100"}`)
      button.type = "button"
      button.textContent = this.messagesValue.presets[key]
      button.addEventListener("click", () => this.pickPreset(key))
      list.append(button)
    })
    return list
  }

  timeList() {
    const datetime = this.modeValue === "datetime"
    const list = this.el("div", `flex flex-col overflow-y-auto overscroll-contain p-1 ${datetime ? "w-24 max-h-[20.75rem] border-l border-gray-100" : "w-36 max-h-64"}`)
    list.setAttribute("role", "listbox")
    list.setAttribute("aria-label", this.messagesValue.choose_time)
    list.addEventListener("keydown", event => this.timeKeydown(event))

    // A saved time off the step grid (14:07) still shows up, in order, so the selection is never hidden.
    const slots = timeSlots(this.minuteStepValue)
    const current = this.time
    if (current && !slots.some(slot => slot.hours === current.hours && slot.minutes === current.minutes)) {
      slots.push(current)
      slots.sort((a, b) => a.hours * 60 + a.minutes - (b.hours * 60 + b.minutes))
    }

    slots.forEach(slot => {
      const selected = !!current && current.hours === slot.hours && current.minutes === slot.minutes
      const button = this.el("button", `shrink-0 rounded-md px-3 py-1.5 text-center text-sm tabular-nums transition-colors cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary/60 ${selected ? "bg-secondary text-white font-semibold" : "text-gray-700 hover:bg-gray-100"}`)
      button.type = "button"
      button.textContent = toISOTime(slot)
      button.dataset.time = toISOTime(slot)
      button.setAttribute("role", "option")
      button.setAttribute("aria-selected", String(selected))
      button.addEventListener("click", () => this.pickTime(slot))
      list.append(button)
    })
    return list
  }

  // Centers the chosen time, or the nearest slot to now, so the list never opens at midnight.
  scrollTimeIntoView() {
    if (!this.hasTimeList) return
    const list = this.popoverTarget.querySelector("[role='listbox']")
    const now = new Date()
    const reference = this.time || { hours: now.getHours(), minutes: now.getMinutes() }
    const target = Array.from(list.querySelectorAll("[data-time]"))
      .find(button => button.dataset.time >= toISOTime(reference)) || list.lastElementChild
    if (target) list.scrollTop = target.offsetTop - list.clientHeight / 2 + target.offsetHeight / 2
  }

  // ---- helpers ----

  el(tag, className = "") {
    const node = document.createElement(tag)
    if (className) node.className = className
    return node
  }

  icon(path, className) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
    svg.setAttribute("viewBox", "0 0 24 24")
    svg.setAttribute("fill", "none")
    svg.setAttribute("stroke", "currentColor")
    svg.setAttribute("stroke-width", "2")
    svg.setAttribute("stroke-linecap", "round")
    svg.setAttribute("stroke-linejoin", "round")
    svg.setAttribute("aria-hidden", "true")
    svg.setAttribute("class", className)
    const line = document.createElementNS("http://www.w3.org/2000/svg", "path")
    line.setAttribute("d", path)
    svg.append(line)
    return svg
  }
}
